import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { useState, useEffect } from 'react';

// Components
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import AccountUpgrade from './components/auth/AccountUpgrade';
import EmailVerification from './components/auth/EmailVerification';
import Dashboard from './components/dashboard/Dashboard';
import UserProfile from './components/dashboard/UserProfile';
import SearchPage from './components/search/SearchPage';
import SavedPlaces from './components/places/SavedPlaces';
import LeadsManagement from './components/leads/LeadsManagement';

// Crawling Components
import CrawlingDashboard from './components/crawling/CrawlingDashboard';
import CrawlTasks from './components/crawling/CrawlTasks';
import SocialMediaLinks from './components/crawling/SocialMediaLinks';
import CrawlingStats from './components/crawling/CrawlingStats';

// NEW: Intelligent Search Components
import IntelligentSearchPage from './components/intelligent/IntelligentSearchPage';
import IntelligentSearchDashboard from './components/intelligent/IntelligentSearchDashboard';
import IntelligentSearchAreas from './components/intelligent/IntelligentSearchAreas';
import IntelligentSearchHistory from './components/intelligent/IntelligentSearchHistory';

import Loading from './components/common/Loading';
import Documentation from './components/documentation/Documentation';

// Styles
import './App.css';

// Mobile Detection Component
const MobileBlocker = ({ children }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (isMobile) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          maxWidth: '400px',
          padding: '30px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <h1 style={{ fontSize: '24px', marginBottom: '16px', color: '#333' }}>
            Desktop Only
          </h1>
          <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.5' }}>
            This application is only available on desktop devices. Please access it from a computer to continue.
          </p>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '16px' }}>
            Current width: {window.innerWidth}px (Min required: 769px)
          </p>
        </div>
      </div>
    );
  }

  return children;
};

// Private Route wrapper that uses Auth Context
const ProtectedRoute = ({ children, requireAuth = true, requireEmailVerification = true }) => {
  const { user, loading, isAuthenticated, isEmailVerified } = useAuth();

  if (loading) {
    return <Loading />;
  }

  if (requireAuth && !isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (requireAuth && requireEmailVerification && user && !user.isAnonymous && !isEmailVerified()) {
    return <Navigate to="/verify-email" replace />;
  }

  return children;
};

// Auto Guest Login Component
const AutoGuestLogin = () => {
  const { isAuthenticated, signInAnonymously, loading } = useAuth();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    const handleAutoLogin = async () => {
      if (!loading && !isAuthenticated() && !isLoggingIn) {
        setIsLoggingIn(true);
        await signInAnonymously();
        navigate('/dashboard');
      } else if (!loading && isAuthenticated()) {
        navigate('/dashboard');
      }
    };

    handleAutoLogin();
  }, [loading, isAuthenticated, signInAnonymously, navigate, isLoggingIn]);

  return <Loading />;
};

// Main App Routes Component
const AppRoutes = () => {
  const { permissions } = useAuth();
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <UserProfile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/upgrade" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <AccountUpgrade />
          </ProtectedRoute>
        } 
      />
      
      {/* Manual Lead Generation Routes */}
      <Route 
        path="/search" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <SearchPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/places" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <SavedPlaces />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/leads" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <LeadsManagement />
          </ProtectedRoute>
        } 
      />

      {/* Intelligent Search Routes - Admin Only */}
      {permissions.canAccessIntelligentSearch && (
      <>
      <Route 
        path="/intelligent-search" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <IntelligentSearchPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/intelligent-search/dashboard" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <IntelligentSearchDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/intelligent-search/areas" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <IntelligentSearchAreas />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/intelligent-search/history" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <IntelligentSearchHistory />
          </ProtectedRoute>
        } 
      />
      </>
      )}

      {/* Crawling Routes */}
      <Route 
        path="/crawling" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <CrawlingDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/crawling/dashboard" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <CrawlingDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/crawling/tasks" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <CrawlTasks />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/crawling/links" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <SocialMediaLinks />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/crawling/stats" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <CrawlingStats />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/documentation" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <Documentation />
          </ProtectedRoute>
        } 
      />
      
      {/* Email Verification Route */}
      <Route 
        path="/verify-email" 
        element={
          <ProtectedRoute requireEmailVerification={false}>
            <EmailVerification />
          </ProtectedRoute>
        } 
      />
      
      {/* Default redirect - Auto guest login for new users */}
      <Route path="/" element={<AutoGuestLogin />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <MobileBlocker>
        <AuthProvider>
          <Router>
            <AppRoutes />
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#4ade80',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 4000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </Router>
        </AuthProvider>
      </MobileBlocker>
    </div>
  );
}

export default App;